import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { SearchResult } from "../../../utils/apiClient";
import { useOrg } from "../../../contexts/OrgContext";

type SearchResultsProps = {
  results: SearchResult | null;
  loading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSelect: () => void;
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  loading,
  isOpen,
  onClose,
  onSelect,
}) => {
  const navigate = useNavigate();
  const { navigateOrgUrl } = useOrg();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const allItems = [
    ...(results?.cases.map((c) => ({ type: "case" as const, ...c })) ?? []),
    ...(results?.files.map((f) => ({ type: "file" as const, ...f })) ?? []),
  ];

  const handleSelect = useCallback(
    (item: (typeof allItems)[number]) => {
      if (item.type === "case") {
        navigate(navigateOrgUrl(`/cases/${item.id}`));
      } else {
        // Navigate to the file detail page
        navigate(navigateOrgUrl(`/files/${item.id}`));
      }
      onSelect();
    },
    [navigate, navigateOrgUrl, onSelect]
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < allItems.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < allItems.length) {
            handleSelect(allItems[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, allItems, selectedIndex, handleSelect, onClose]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".search-input")
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (!isOpen) return null;

  const hasResults =
    results && (results.cases.length > 0 || results.files.length > 0);

  return (
    <div
      ref={dropdownRef}
      className="absolute left-0 right-0 top-full mt-2 z-50 rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900 max-h-[400px] overflow-y-auto"
    >
      {loading && (
        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          Searching...
        </div>
      )}

      {!loading && !hasResults && results && (
        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          No results found
        </div>
      )}

      {!loading && hasResults && (
        <>
          {results.cases.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
                Cases
              </div>
              {results.cases.map((caseItem, index) => (
                <button
                  key={caseItem.id}
                  onClick={() =>
                    handleSelect({ type: "case" as const, ...caseItem })
                  }
                  className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    selectedIndex === index
                      ? "bg-gray-100 dark:bg-gray-800"
                      : ""
                  }`}
                >
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/30">
                    <svg
                      className="w-4 h-4 text-brand-600 dark:text-brand-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {caseItem.title}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <span
                        className={`inline-flex px-1.5 py-0.5 rounded text-xs ${
                          caseItem.status === "open"
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {caseItem.status}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.files.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
                Files
              </div>
              {results.files.map((file, index) => {
                const itemIndex = results.cases.length + index;
                return (
                  <button
                    key={file.id}
                    onClick={() =>
                      handleSelect({ type: "file" as const, ...file })
                    }
                    className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      selectedIndex === itemIndex
                        ? "bg-gray-100 dark:bg-gray-800"
                        : ""
                    }`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                      <svg
                        className="w-4 h-4 text-gray-600 dark:text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {file.filename}
                      </div>
                      {file.caseTitle && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          in {file.caseTitle}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
